import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, type ComputedRef } from 'vue';
import { type RouteLocation, useRoute, useRouter } from 'vue-router';

export interface UseRoutingReturn {
  routeMetadata: ComputedRef<Record<string, any>>;
  routePrefix: ComputedRef<string | unknown>;
  routePageName: ComputedRef<string | undefined>;
  normalizedFullPath: ComputedRef<string | undefined>;
  getPageRoute: (
    pageName: string,
    language: HgSupportedLanguage,
    options?: any,
    ignorePrefix?: boolean,
  ) => {
    name: string;
    [key: string]: any;
  };
}

export function useRouting(): UseRoutingReturn {
  const route = useRoute();
  const router = useRouter();

  const routeMetadata = computed(() => route.meta || {});

  const routePrefix = computed(() => routeMetadata.value.prefix || '');

  const routePageName = computed(() => {
    if (!route.name) {
      return undefined;
    }
    return (route.name as string).split('-')[0];
  });

  const normalizedFullPath = computed(() => {
    const normalizedPageRoute = {
      params: route.params,
      ...getPageRoute(route.name as string, HgSupportedLanguage.EN),
    };

    const resolvedRoute = router.resolve(normalizedPageRoute as RouteLocation);

    return resolvedRoute.fullPath.replace('/v6', '');
  });

  function extractPageName(pageName: string): string {
    return pageName.replace(/-[a-z]{2}$/, '');
  }

  function getPageRoute(
    pageName: string,
    language: HgSupportedLanguage,
    options?: any,
    ignorePrefix = false,
  ) {
    return {
      name: `${extractPageName(pageName)}-${language}${ignorePrefix ? '' : routePrefix.value}`,
      ...options,
    };
  }

  return {
    routeMetadata,
    routePrefix,
    routePageName,
    normalizedFullPath,
    getPageRoute,
  };
}
